import { useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, History, Info, MoveHorizontal, Pencil, Play, RotateCcw, Sparkles, Timer, X } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge, stateVariant } from '@/components/ui/badge';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { SortableTH, Table, TBody, TD, THead, TR } from '@/components/ui/table';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/data/EmptyState';
import { useToast } from '@/components/ui/toast';
import { useAuth } from '@/components/auth/AuthProvider';
import {
  useDeleteOverride,
  useEffectiveJobs,
  useOptimizeSchedule,
  useQueueAdminAction,
  useRecentAdminActions,
  useUpsertOverride,
  type JobRow as JobRowData,
} from '@/hooks/useCronJobs';
import { ScheduleHistoryDrawer } from '@/components/cron/ScheduleHistoryDrawer';
import { describeCron } from '@/lib/cron';
import {
  clampMinute,
  detectConflicts,
  expandCronMinutes,
  fireStepMinutes,
  isDraggableCron,
  shiftCronMinute,
  LEVEL_DURATION_LABEL,
  LEVEL_DURATION_MINUTES,
  LEVEL_STROKE_WIDTH,
} from '@/lib/cronSchedule';
import {
  commitCronChanges,
  previewCronChanges,
  HOST_API_CONFIGURED,
  type CronChange,
  type PreviewResult,
  type ScheduleScope,
} from '@/lib/hostApi';
import { useTableSort } from '@/lib/useTableSort';
import { cn, formatRelative } from '@/lib/utils';
import { PROVIDERS } from '@/config/providers';
import type { JobLevel } from '@/config/jobs';

const PROVIDER_OPTIONS = ['', ...PROVIDERS.map((p) => p.key)];
const KIND_OPTIONS = ['', 'harvest', 'sync'];

// Explicit dark-cobalt tokens from the redesign spec §3. These intentionally
// sit outside the Tailwind theme vars because the timeline encodes meaning in
// exact hues (cyan = frequent, purple = sparse, red = conflict) that must stay
// stable regardless of future theme tweaks.
const C = {
  surface: '#0E1626',
  surfaceRaised: '#111B30',
  surfaceDarker: '#0A1120',
  trackBg: '#131D32',
  border: '#1E2A40',
  rowDivider: '#131D32',
  textSecondary: '#94A3B8',
  textTertiary: '#64748B',
  cyan: '#5EE3F7',
  purple: '#A78BFA',
  red: '#F87171',
  ghost: '#1A2540',
};
const RAMP = ['#1A3057', '#1E40AF', '#2563EB', '#5EE3F7'];
const VIEW_W = 660;
const COLLAPSE_KEY = 'cron-collapsed-queues';

type Zoom = 'hour' | 'day' | 'week';

const minuteX = (m: number) => m * (VIEW_W / 60);
const dotColor = (step: number) => (step <= 5 ? C.cyan : C.purple);
const glowColor = (step: number) => (step <= 5 ? 'rgba(94,227,247,0.25)' : 'rgba(167,139,250,0.25)');

// A fire at minute `m` occupies the worker for ~LEVEL_DURATION_MINUTES[level],
// drawn as a segment from m extending right, clamped to the end of the hour.
// L1 is ~1 min, so we floor the pixel width to keep it visibly clickable.
const MIN_SEGMENT_PX = 7;
function segmentGeom(m: number, level: JobLevel) {
  const x = minuteX(m);
  const endMinute = Math.min(60, m + LEVEL_DURATION_MINUTES[level]);
  const width = Math.max(MIN_SEGMENT_PX, minuteX(endMinute) - x);
  return { x, width, streams: level === 'L4' };
}

interface JobView extends JobRowData {
  fires: number[];
  step: number;
  isOverridden: boolean;
  // True when the user has dragged/nudged this job but not yet committed.
  isPending: boolean;
}

export function CronJobsRoute() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { jobs, isLoading, isError, error } = useEffectiveJobs();
  const upsert = useUpsertOverride();
  const remove = useDeleteOverride();
  const queueAction = useQueueAdminAction();
  const actions = useRecentAdminActions(12);
  const toast = useToast();

  // Recent admin actions table (6 cols). Default is requested_at desc from the
  // hook; feeding rows in with no initial key preserves it. The Action column
  // sorts by the same humanized label it renders.
  const actionsSort = useTableSort(actions.data ?? [], {
    accessors: {
      action_type: (a) => a.action_type.replaceAll('_', ' '),
    },
  });

  const [providerFilter, setProviderFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [search, setSearch] = useState('');
  const [zoom, setZoom] = useState<Zoom>('hour');
  const [editing, setEditing] = useState<JobRowData | null>(null);
  // Per-job in-flight triggers, so one running trigger doesn't disable every
  // row's Run button (the shared mutation's isPending would).
  const [pendingJobs, setPendingJobs] = useState<Set<string>>(new Set());
  const [inspector, setInspector] = useState<string | null>(null);

  // Phase 2: uncommitted schedule edits — jobId → proposed effective cron.
  // The timeline + conflicts render from these so dragging updates rings live;
  // nothing persists until the commit modal succeeds.
  const [pending, setPending] = useState<Map<string, string>>(new Map());
  const [committing, setCommitting] = useState(false);
  const [showCommit, setShowCommit] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const optimize = useOptimizeSchedule();
  // Snapshot of the changes the commit modal previews/commits, frozen when the
  // modal opens. Passing the live `pendingChanges` would re-fire the preview if
  // React Query refetches `jobs` mid-confirmation (new array reference).
  const [stagedChanges, setStagedChanges] = useState<CronChange[]>([]);

  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...collapsed]));
  }, [collapsed]);

  // Schedule geometry is derived from the *full* catalog, not the filtered
  // view: a conflict ring on a visible row may be caused by a job the current
  // filter hides, which is exactly the cross-cutting contention we want to
  // surface. Search/filters only narrow which rows render, never the analytics.
  // Effective schedule with uncommitted edits layered on top of saved overrides.
  const proposed = useMemo(
    () =>
      jobs.map((j) => {
        const draft = pending.get(j.jobId);
        return {
          ...j,
          effectiveCron: draft ?? j.effectiveCron,
          isPending: draft !== undefined,
        };
      }),
    [jobs, pending],
  );

  const views = useMemo<JobView[]>(
    () =>
      proposed.map((j) => {
        const fires = j.enabled ? expandCronMinutes(j.effectiveCron) : [];
        return { ...j, fires, step: fireStepMinutes(fires), isOverridden: !!j.override };
      }),
    [proposed],
  );

  // Conflicts/density reflect the PROPOSED state so rings update as you drag.
  const conflicts = useMemo(() => detectConflicts(proposed), [proposed]);
  const overriddenCount = useMemo(() => jobs.filter((j) => j.override).length, [jobs]);

  // Heatmap density: concurrent fires per minute across every queue.
  const density = useMemo(() => {
    const counts = new Array(60).fill(0);
    for (const v of views) for (const m of v.fires) counts[m] += 1;
    return counts;
  }, [views]);
  const conflictMinutes = useMemo(() => {
    const set = new Set<number>();
    for (const minutes of conflicts.byQueue.values()) for (const m of minutes) set.add(m);
    return set;
  }, [conflicts]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return views.filter((j) => {
      if (providerFilter && j.provider !== providerFilter) return false;
      if (kindFilter && j.kind !== kindFilter) return false;
      if (needle && !j.label.toLowerCase().includes(needle) && !j.jobId.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [views, providerFilter, kindFilter, search]);

  // Queue groups in first-seen catalog order (Genius before Presto).
  const groups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, JobView[]>();
    for (const j of filtered) {
      const bucket = map.get(j.queue);
      if (bucket) bucket.push(j);
      else {
        map.set(j.queue, [j]);
        order.push(j.queue);
      }
    }
    return order.map((queue) => ({ queue, jobs: map.get(queue)! }));
  }, [filtered]);

  async function onTrigger(job: JobView) {
    setPendingJobs((prev) => new Set(prev).add(job.jobId));
    try {
      await queueAction.mutateAsync({
        action_type: 'trigger_job',
        job_id: job.jobId,
        payload: {},
        requested_by: user?.id ?? null,
      });
      toast.push('success', `Queued trigger for ${job.jobId}`);
    } catch (e) {
      toast.push('error', e instanceof Error ? e.message : 'Failed to queue action');
    } finally {
      setPendingJobs((prev) => {
        const next = new Set(prev);
        next.delete(job.jobId);
        return next;
      });
    }
  }

  function toggleQueue(queue: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(queue)) next.delete(queue);
      else next.add(queue);
      return next;
    });
  }

  // Drag/keyboard reschedule: re-phase the job's cron series by the drag delta
  // and stage it as a pending edit. Dropping a job back onto its saved schedule
  // clears the pending entry.
  function reschedule(jobId: string, fromMinute: number, toMinute: number) {
    setPending((prev) => {
      const job = jobs.find((j) => j.jobId === jobId);
      if (!job) return prev;
      const current = prev.get(jobId) ?? job.effectiveCron;
      const nextCron = shiftCronMinute(current, fromMinute, toMinute);
      if (!nextCron || nextCron === current) return prev;
      const next = new Map(prev);
      if (nextCron === job.effectiveCron) next.delete(jobId);
      else next.set(jobId, nextCron);
      return next;
    });
  }

  const pendingChanges = useMemo<CronChange[]>(
    () =>
      [...pending.entries()].map(([jobId, cron]) => {
        const job = jobs.find((j) => j.jobId === jobId);
        return {
          jobId,
          cronExpression: cron,
          queue: job?.override?.queue ?? null,
          enabled: job?.enabled ?? true,
          reason: 'Rescheduled via timeline',
        };
      }),
    [pending, jobs],
  );

  // Optimizer: ask the Host (authoritative registry + real queues) for a
  // re-balanced, conflict-minimized, dependency-respecting proposal, then stage
  // it as pending edits so it flows through the existing review & commit path.
  async function onOptimize(scope: ScheduleScope) {
    try {
      const result = await optimize.mutateAsync(scope);
      if (result.proposed.length === 0) {
        toast.push('info', 'Schedule already optimal — no changes proposed.');
        return;
      }
      setPending((prev) => {
        const next = new Map(prev);
        for (const c of result.proposed) next.set(c.jobId, c.cronExpression);
        return next;
      });
      // Headline the meaningful win: lower peak per-minute concurrency (gentler on
      // the upstream APIs) rather than the raw conflicted-minute count, which can
      // rise when load is deliberately spread out.
      const peakBefore = Math.max(0, ...result.before.queues.map((q) => q.peakConcurrency));
      const peakAfter = Math.max(0, ...result.after.queues.map((q) => q.peakConcurrency));
      const peakNote = peakAfter < peakBefore ? `, peak load ${peakBefore}→${peakAfter}/min` : '';
      toast.push(
        'success',
        `Optimizer proposed ${result.proposed.length} change(s)${peakNote} — review & commit below.`,
      );
    } catch (e) {
      toast.push('error', `Optimize failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function commitPending() {
    setCommitting(true);
    try {
      const result = await commitCronChanges(stagedChanges);
      toast.push('success', `Committed ${result.applied.length} schedule change(s); rescheduled live.`);
      setPending(new Map());
      await queryClient.invalidateQueries({ queryKey: ['cron-overrides'] });
      return true;
    } catch (e) {
      toast.push('error', `Commit failed: ${e instanceof Error ? e.message : String(e)}`);
      return false;
    } finally {
      setCommitting(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Cron jobs"
        description="Recurring-job catalog from HarvestSchedulerBootstrap.cs joined against harvest.cron_schedule_overrides, grouped by queue to surface same-worker serialization. Edit to override schedule/queue/enabled without a code deploy."
      />

      <SummaryStatsBar total={jobs.length} conflicts={conflicts.pairCount} overridden={overriddenCount} />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search by label or id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-56"
          aria-label="Search jobs by label or id"
        />
        <Select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} className="w-36" aria-label="Filter by provider">
          {PROVIDER_OPTIONS.map((p) => (
            <option key={p} value={p}>{p || 'All providers'}</option>
          ))}
        </Select>
        <Select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="w-32" aria-label="Filter by kind">
          {KIND_OPTIONS.map((k) => (
            <option key={k} value={k}>{k || 'All kinds'}</option>
          ))}
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <OptimizeControl
            busy={optimize.isPending}
            disabled={!HOST_API_CONFIGURED}
            disabledReason={HOST_API_CONFIGURED ? undefined : 'Set VITE_HOST_API_URL to enable the optimizer'}
            onOptimize={onOptimize}
          />
          <Button
            variant="outline"
            onClick={() => setHistoryOpen(true)}
            disabled={!HOST_API_CONFIGURED}
            title={HOST_API_CONFIGURED ? undefined : 'Set VITE_HOST_API_URL to view history'}
          >
            <History className="mr-1.5 h-4 w-4" /> History
          </Button>
          <TimeZoomToggle value={zoom} onChange={setZoom} />
        </div>
      </div>

      {isError ? (
        <ErrorState error={error} />
      ) : isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          <DensityHeatmap
            zoom={zoom}
            density={density}
            conflictMinutes={conflictMinutes}
            onInspect={setInspector}
          />

          <LevelLegend />

          {zoom === 'hour' && (
            <div className="flex items-center gap-1.5 px-1 text-[11px]" style={{ color: C.textTertiary }}>
              <MoveHorizontal className="h-3.5 w-3.5" style={{ color: C.cyan }} />
              <span>Drag a fire to reschedule it — changes stage below and only go live after you review &amp; commit.</span>
            </div>
          )}

          <div
            className="overflow-hidden rounded-lg border"
            style={{ background: C.surface, borderColor: C.border }}
          >
            <div
              className="grid items-center px-3.5 py-2.5"
              style={{ gridTemplateColumns: '220px 1fr 175px', background: C.surfaceRaised, borderBottom: `1px solid ${C.border}` }}
            >
              <span className="text-[10px] font-medium tracking-wider" style={{ color: C.textTertiary }}>JOB</span>
              <AxisSvg zoom={zoom} />
              <span className="text-right text-[10px] font-medium tracking-wider" style={{ color: C.textTertiary }}>ACTIONS</span>
            </div>

            {groups.length === 0 ? (
              <div className="px-3.5 py-8 text-center text-xs" style={{ color: C.textTertiary }}>
                No jobs match the current filters.
              </div>
            ) : (
              groups.map(({ queue, jobs: queueJobs }) => (
                <QueueGroup
                  key={queue}
                  queue={queue}
                  jobs={queueJobs}
                  conflictCount={conflicts.byQueue.get(queue)?.size ?? 0}
                  collapsed={collapsed.has(queue)}
                  onToggle={() => toggleQueue(queue)}
                  conflictsByJob={conflicts.byJob}
                  zoom={zoom}
                  onInspect={setInspector}
                  onRun={onTrigger}
                  onEdit={setEditing}
                  onReschedule={reschedule}
                  pendingJobs={pendingJobs}
                />
              ))
            )}
          </div>
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Recent admin actions</CardTitle>
          <CardDescription>Drained by the .NET host poller; status updates appear here.</CardDescription>
        </CardHeader>
        <CardContent>
          {actions.isError ? (
            <ErrorState error={actions.error} />
          ) : actions.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : actions.data?.length ? (
            <Table>
              <THead>
                <TR>
                  <SortableTH label="Action" sortKey="action_type" active={actionsSort.sortKey === 'action_type'} direction={actionsSort.sortDir} onSort={actionsSort.toggle} />
                  <SortableTH label="Job" sortKey="job_id" active={actionsSort.sortKey === 'job_id'} direction={actionsSort.sortDir} onSort={actionsSort.toggle} />
                  <SortableTH label="Status" sortKey="status" active={actionsSort.sortKey === 'status'} direction={actionsSort.sortDir} onSort={actionsSort.toggle} />
                  <SortableTH label="Requested" sortKey="requested_at" active={actionsSort.sortKey === 'requested_at'} direction={actionsSort.sortDir} onSort={actionsSort.toggle} />
                  <SortableTH label="Processed" sortKey="processed_at" active={actionsSort.sortKey === 'processed_at'} direction={actionsSort.sortDir} onSort={actionsSort.toggle} />
                  <SortableTH label="Hangfire job" sortKey="hangfire_job_id" active={actionsSort.sortKey === 'hangfire_job_id'} direction={actionsSort.sortDir} onSort={actionsSort.toggle} />
                </TR>
              </THead>
              <TBody>
                {actionsSort.sorted.map((a) => (
                  <TR key={a.action_id}>
                    <TD className="capitalize">{a.action_type.replaceAll('_', ' ')}</TD>
                    <TD className="font-mono text-xs">{a.job_id ?? '—'}</TD>
                    <TD>
                      <Badge variant={stateVariant(a.status)}>{a.status}</Badge>
                    </TD>
                    <TD>{formatRelative(a.requested_at)}</TD>
                    <TD>{a.processed_at ? formatRelative(a.processed_at) : '—'}</TD>
                    <TD className="font-mono text-xs">
                      {a.hangfire_job_id ? (
                        <Link
                          to={`/hangfire?job=${encodeURIComponent(a.hangfire_job_id)}`}
                          className="text-primary hover:underline"
                        >
                          {a.hangfire_job_id}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Timer className="h-3.5 w-3.5" /> No actions queued yet.
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <EditOverrideDialog
          job={editing}
          canReset={!!editing.override}
          resetting={remove.isPending}
          onReset={async () => {
            try {
              await remove.mutateAsync(editing.jobId);
              toast.push('info', `Reset ${editing.jobId} to code default`);
              setEditing(null);
            } catch (e) {
              toast.push('error', `Reset failed for ${editing.jobId}: ${e instanceof Error ? e.message : String(e)}`);
            }
          }}
          onClose={() => setEditing(null)}
          onSave={async (form) => {
            try {
              await upsert.mutateAsync({
                job_id: editing.jobId,
                cron_expression: form.cron,
                queue: form.queue || null,
                enabled: form.enabled,
                reason: form.reason || null,
                updated_by: user?.id ?? null,
              });
              toast.push('success', `Saved override for ${editing.jobId}`);
              setEditing(null);
            } catch (e) {
              toast.push('error', `Save failed for ${editing.jobId}: ${e instanceof Error ? e.message : String(e)}`);
            }
          }}
        />
      )}

      {showCommit && (
        <CommitConfirmationModal
          changes={stagedChanges}
          committing={committing}
          onCancel={() => setShowCommit(false)}
          onConfirm={async () => {
            const ok = await commitPending();
            if (ok) setShowCommit(false);
          }}
        />
      )}

      {/* Spacer so the fixed bottom bars never hide the last card. */}
      <div aria-hidden className="h-24" />
      {pending.size > 0 && (
        <PendingChangesBar
          count={pending.size}
          conflicts={conflicts.pairCount}
          disabled={!HOST_API_CONFIGURED || committing}
          disabledReason={HOST_API_CONFIGURED ? undefined : 'Set VITE_HOST_API_URL to enable commits'}
          onDiscard={() => setPending(new Map())}
          onReview={() => {
            setStagedChanges(pendingChanges);
            setShowCommit(true);
          }}
        />
      )}
      <InspectorStrip text={inspector} />
      <ScheduleHistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
}

function OptimizeControl({
  busy,
  disabled,
  disabledReason,
  onOptimize,
}: {
  busy: boolean;
  disabled: boolean;
  disabledReason?: string;
  onOptimize: (scope: ScheduleScope) => void | Promise<void>;
}) {
  const [scope, setScope] = useState<ScheduleScope>('both');
  return (
    <div className="flex items-center gap-1.5" title={disabledReason}>
      <Select
        value={scope}
        onChange={(e) => setScope(e.target.value as ScheduleScope)}
        className="w-28"
        aria-label="Optimizer scope"
        disabled={disabled || busy}
      >
        <option value="both">Genius + Presto</option>
        <option value="genius">Genius</option>
        <option value="presto">Presto</option>
      </Select>
      <Button onClick={() => onOptimize(scope)} disabled={disabled || busy}>
        <Sparkles className="mr-1.5 h-4 w-4" /> {busy ? 'Optimizing…' : 'Optimize'}
      </Button>
    </div>
  );
}

function SummaryStatsBar({ total, conflicts, overridden }: { total: number; conflicts: number; overridden: number }) {
  return (
    <div
      className="flex w-fit items-center gap-3.5 rounded-lg border px-3.5 py-2"
      style={{ background: C.surface, borderColor: C.border }}
    >
      <Stat value={total} label="jobs" />
      <span className="self-stretch border-l" style={{ borderColor: C.border }} />
      <Stat value={conflicts} label="conflicts" color={C.red} />
      <span className="self-stretch border-l" style={{ borderColor: C.border }} />
      <Stat value={overridden} label="overridden" color="#FBBF24" />
    </div>
  );
}

function Stat({ value, label, color }: { value: number; label: string; color?: string }) {
  return (
    <div>
      <span className="text-[15px] font-medium" style={{ color: color ?? '#F1F5F9' }}>{value}</span>
      <span className="ml-1 text-[11px]" style={{ color: C.textSecondary }}>{label}</span>
    </div>
  );
}

const ZOOMS: Zoom[] = ['hour', 'day', 'week'];

function TimeZoomToggle({ value, onChange }: { value: Zoom; onChange: (z: Zoom) => void }) {
  return (
    <div
      role="radiogroup"
      aria-label="Time zoom"
      className="inline-flex overflow-hidden rounded-lg border"
      style={{ background: C.surface, borderColor: C.border }}
      onKeyDown={(e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const i = ZOOMS.indexOf(value);
        const next = e.key === 'ArrowRight' ? (i + 1) % ZOOMS.length : (i - 1 + ZOOMS.length) % ZOOMS.length;
        onChange(ZOOMS[next]);
      }}
    >
      {ZOOMS.map((z, i) => {
        const active = z === value;
        return (
          <button
            key={z}
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(z)}
            className="px-3.5 py-1.5 text-xs capitalize transition-colors"
            style={{
              background: active ? 'rgba(94,227,247,0.12)' : 'transparent',
              color: active ? C.cyan : C.textSecondary,
              borderLeft: i === 0 ? undefined : `1px solid ${C.border}`,
            }}
          >
            {z}
          </button>
        );
      })}
    </div>
  );
}

function DensityHeatmap({
  zoom,
  density,
  conflictMinutes,
  onInspect,
}: {
  zoom: Zoom;
  density: number[];
  conflictMinutes: Set<number>;
  onInspect: (text: string | null) => void;
}) {
  const title =
    zoom === 'hour'
      ? 'Concurrent fires per minute · all queues'
      : zoom === 'day'
        ? 'Fires per hour · 24h view'
        : 'Fires per day · 7-day view';
  const sub =
    zoom === 'hour'
      ? 'Red columns indicate queue conflicts'
      : zoom === 'day'
        ? 'Aggregated across all queues (synthetic — every job runs hourly)'
        : 'Synthetic — all jobs currently run every day';

  return (
    <div className="rounded-lg border px-4 py-3.5" style={{ background: C.surface, borderColor: C.border }}>
      <div className="mb-2.5 flex items-baseline justify-between">
        <div>
          <span className="text-xs font-medium">{title}</span>
          <span className="ml-2 text-[11px]" style={{ color: C.textTertiary }}>{sub}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px]" style={{ color: C.textTertiary }}>
          <span>0</span>
          <span className="inline-block h-2 w-3 rounded-sm" style={{ background: C.ghost }} />
          {RAMP.slice(1).map((c) => (
            <span key={c} className="inline-block h-2 w-3 rounded-sm" style={{ background: c }} />
          ))}
          <span className="inline-block h-2 w-3" style={{ background: C.red }} />
          <span>conflict</span>
        </div>
      </div>
      <svg viewBox="0 0 660 70" preserveAspectRatio="none" className="block h-[70px] w-full" role="img" aria-label={title}>
        {zoom === 'hour' && <HourHeatmap density={density} conflictMinutes={conflictMinutes} onInspect={onInspect} />}
        {zoom === 'day' && <SyntheticHeatmap cols={24} fill="#2563EB" pad={2} />}
        {zoom === 'week' && <SyntheticHeatmap cols={7} fill="#2563EB" pad={4} labels={['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']} />}
        {zoom !== 'week' && <AxisLabels labels={zoom === 'hour' ? HOUR_LABELS : DAY_LABELS} y={50} />}
      </svg>
    </div>
  );
}

function HourHeatmap({
  density,
  conflictMinutes,
  onInspect,
}: {
  density: number[];
  conflictMinutes: Set<number>;
  onInspect: (text: string | null) => void;
}) {
  const colW = VIEW_W / 60;
  const maxD = Math.max(...density, 1);
  return (
    <>
      {density.map((v, m) => {
        const r = v / maxD;
        let fill = C.ghost;
        if (conflictMinutes.has(m)) fill = C.red;
        else if (r > 0.6) fill = RAMP[3];
        else if (r > 0.4) fill = RAMP[2];
        else if (r > 0.2) fill = RAMP[1];
        else if (r > 0) fill = RAMP[0];
        return (
          <rect
            key={m}
            x={m * colW}
            y={0}
            width={colW - 0.6}
            height={50}
            rx={1.5}
            fill={fill}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() =>
              onInspect(
                `Minute :${String(m).padStart(2, '0')} — ${v} fire${v === 1 ? '' : 's'}${conflictMinutes.has(m) ? ' · ⚠ same-queue conflict' : ''}`,
              )
            }
            onMouseLeave={() => onInspect(null)}
          />
        );
      })}
    </>
  );
}

function SyntheticHeatmap({ cols, fill, pad, labels }: { cols: number; fill: string; pad: number; labels?: string[] }) {
  const colW = VIEW_W / cols;
  return (
    <>
      {Array.from({ length: cols }, (_, i) => (
        <rect key={i} x={i * colW + pad} y={0} width={colW - pad * 2} height={50} rx={2} fill={fill} />
      ))}
      {labels?.map((l, i) => (
        <text key={l} x={i * colW + colW / 2} y={66} textAnchor="middle" fontSize={10} fill={C.textSecondary}>
          {l}
        </text>
      ))}
    </>
  );
}

const HOUR_LABELS = [':00', ':10', ':20', ':30', ':40', ':50', ':59'];
const DAY_LABELS = ['00h', '04h', '08h', '12h', '16h', '20h', '23h'];
const WEEK_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function AxisLabels({ labels, y }: { labels: string[]; y: number }) {
  return (
    <>
      {labels.map((l, i) => (
        <text
          key={l}
          x={(VIEW_W / (labels.length - 1)) * i}
          y={y + 16}
          textAnchor={i === labels.length - 1 ? 'end' : 'start'}
          fontSize={9}
          fill={C.textTertiary}
        >
          {l}
        </text>
      ))}
    </>
  );
}

function AxisSvg({ zoom }: { zoom: Zoom }) {
  const labels = zoom === 'hour' ? HOUR_LABELS : zoom === 'day' ? DAY_LABELS : WEEK_LABELS;
  return (
    <svg viewBox="0 0 660 14" preserveAspectRatio="none" className="h-3.5 w-full" aria-hidden="true">
      {labels.map((l, i) => (
        <text
          key={l}
          x={(VIEW_W / (labels.length - 1)) * i}
          y={10}
          textAnchor={i === labels.length - 1 ? 'end' : 'start'}
          fontSize={9}
          fill={C.textTertiary}
        >
          {l}
        </text>
      ))}
    </svg>
  );
}

function QueueGroup({
  queue,
  jobs,
  conflictCount,
  collapsed,
  onToggle,
  conflictsByJob,
  zoom,
  onInspect,
  onRun,
  onEdit,
  onReschedule,
  pendingJobs,
}: {
  queue: string;
  jobs: JobView[];
  conflictCount: number;
  collapsed: boolean;
  onToggle: () => void;
  conflictsByJob: Map<string, Set<number>>;
  zoom: Zoom;
  onInspect: (text: string | null) => void;
  onRun: (job: JobView) => void;
  onEdit: (job: JobRowData) => void;
  onReschedule: (jobId: string, fromMinute: number, toMinute: number) => void;
  pendingJobs: Set<string>;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex w-full items-center justify-between px-3.5 py-2.5 text-left"
        style={{ background: C.surfaceDarker, borderBottom: `1px solid ${C.border}` }}
      >
        <div className="flex items-center gap-2">
          <ChevronDown
            className="h-3.5 w-3.5 transition-transform"
            style={{ color: C.textSecondary, transform: collapsed ? 'rotate(-90deg)' : 'none' }}
          />
          <span className="font-mono text-[11px] font-medium" style={{ color: '#CBD5E1' }}>{queue}</span>
          <span className="rounded-full px-1.5 py-0.5 text-[10px]" style={{ background: C.ghost, color: C.textSecondary }}>
            {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
          </span>
          {conflictCount > 0 && (
            <span
              className="rounded-full border px-1.5 py-0.5 text-[10px]"
              style={{ color: '#FCA5A5', background: 'rgba(248,113,113,0.12)', borderColor: 'rgba(248,113,113,0.3)' }}
            >
              ⚠ {conflictCount} {conflictCount === 1 ? 'conflict' : 'conflicts'}
            </span>
          )}
        </div>
        <span className="text-[10px]" style={{ color: C.textTertiary }}>serializes on .NET host worker</span>
      </button>

      {!collapsed &&
        jobs.map((job) => (
          <JobRowView
            key={job.jobId}
            job={job}
            conflictMinutes={conflictsByJob.get(job.jobId) ?? EMPTY_SET}
            zoom={zoom}
            onInspect={onInspect}
            onRun={onRun}
            onEdit={onEdit}
            onReschedule={onReschedule}
            runPending={pendingJobs.has(job.jobId)}
          />
        ))}
    </div>
  );
}

const EMPTY_SET = new Set<number>();

function JobRowView({
  job,
  conflictMinutes,
  zoom,
  onInspect,
  onRun,
  onEdit,
  onReschedule,
  runPending,
}: {
  job: JobView;
  conflictMinutes: Set<number>;
  zoom: Zoom;
  onInspect: (text: string | null) => void;
  onRun: (job: JobView) => void;
  onEdit: (job: JobRowData) => void;
  onReschedule: (jobId: string, fromMinute: number, toMinute: number) => void;
  runPending: boolean;
}) {
  const [seg, setSeg] = useState<{ minute: number; midPct: number; conflicting: boolean } | null>(null);
  return (
    <div
      className="row grid items-center px-3.5 py-1.5 transition-colors hover:bg-[#111B30]"
      style={{ gridTemplateColumns: '220px 1fr 175px', borderBottom: `1px solid ${C.rowDivider}` }}
      onMouseEnter={() => {
        const cm = conflictMinutes.size > 0 ? ` · ⚠ ${conflictMinutes.size} conflicting minute(s)` : '';
        onInspect(`${job.level} · ${job.jobId} · ${job.effectiveCron} · queue ${job.queue}${cm}`);
      }}
      onMouseLeave={() => onInspect(null)}
    >
      <div className="flex min-w-0 items-center gap-2">
        <LevelBadge level={job.level} dim={!job.enabled} />
        <div className="min-w-0 overflow-hidden">
          <div className={cn('truncate text-xs font-medium', !job.enabled && 'opacity-60')}>{job.label}</div>
          <div className="truncate font-mono text-[10px]" style={{ color: C.textTertiary }}>{job.effectiveCron}</div>
        </div>
      </div>

      <div className="relative h-6 px-1">
        <TrackSvg
          job={job}
          conflictMinutes={conflictMinutes}
          zoom={zoom}
          onSegHover={setSeg}
          onReschedule={onReschedule}
          onInspect={onInspect}
        />
        {seg && (
          <div
            className="pointer-events-none absolute z-10 -translate-x-1/2 whitespace-nowrap rounded-md border px-2 py-1 text-[10px] shadow-lg"
            style={{
              left: `${seg.midPct}%`,
              bottom: 'calc(100% + 3px)',
              background: C.surfaceRaised,
              borderColor: C.border,
              color: '#E2E8F0',
            }}
          >
            <span className="font-mono" style={{ color: C.textSecondary }}>{job.level}</span>
            {' · '}
            {job.level === 'L4' ? 'streams from' : 'fires'} :{String(seg.minute).padStart(2, '0')}
            {' · '}
            <span style={{ color: C.cyan }}>{LEVEL_DURATION_LABEL[job.level]}</span>
            {seg.conflicting && <span style={{ color: C.red }}> · ⚠ conflict</span>}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-1.5">
        {job.isPending ? (
          <span
            className="rounded-full border px-1.5 py-0.5 text-[9px] tracking-wide"
            style={{ color: C.cyan, background: 'rgba(94,227,247,0.12)', borderColor: 'rgba(94,227,247,0.4)' }}
          >
            PENDING
          </span>
        ) : job.isOverridden ? (
          <span
            className="rounded-full border px-1.5 py-0.5 text-[9px] tracking-wide"
            style={{ color: '#FCD34D', background: 'rgba(251,191,36,0.15)', borderColor: 'rgba(251,191,36,0.35)' }}
          >
            OVERRIDDEN
          </span>
        ) : (
          <span className="rounded-full px-1.5 py-0.5 text-[9px] tracking-wide" style={{ background: C.ghost, color: C.textSecondary }}>
            {job.enabled ? 'DEFAULT' : 'DISABLED'}
          </span>
        )}
        <button
          type="button"
          onClick={() => onRun(job)}
          disabled={runPending}
          aria-label={`Run ${job.label} now`}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] disabled:opacity-50"
          style={{ borderColor: C.border, color: C.cyan }}
        >
          <Play className="h-2.5 w-2.5" /> Run
        </button>
        <button
          type="button"
          onClick={() => onEdit(job)}
          aria-label={`Edit ${job.label}`}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px]"
          style={{ borderColor: C.border, color: C.textSecondary }}
        >
          <Pencil className="h-2.5 w-2.5" /> Edit
        </button>
      </div>
    </div>
  );
}

// Synthetic day/week glyph width per tier (px) — heavier tiers read wider, the
// same magnitude cue the hour view encodes as real duration.
const DAY_SEGMENT_W: Record<JobLevel, number> = { L1: 8, L2: 16, L3: 24, L4: 34 };
const WEEK_SEGMENT_W: Record<JobLevel, number> = { L1: 22, L2: 32, L3: 42, L4: 52 };

function TrackSvg({
  job,
  conflictMinutes,
  zoom,
  onSegHover,
  onReschedule,
  onInspect,
}: {
  job: JobView;
  conflictMinutes: Set<number>;
  zoom: Zoom;
  onSegHover?: (seg: { minute: number; midPct: number; conflicting: boolean } | null) => void;
  onReschedule?: (jobId: string, fromMinute: number, toMinute: number) => void;
  onInspect?: (text: string | null) => void;
}) {
  const color = dotColor(job.step);
  const stroke = LEVEL_STROKE_WIDTH[job.level];
  const y = 12 - stroke / 2;
  const svgRef = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<{ from: number; to: number } | null>(null);

  // Only the hour view is editable, only for enabled jobs whose cron we can
  // safely re-phase. Lists / bare `*` route through the Edit modal instead.
  const editable = zoom === 'hour' && job.enabled && !!onReschedule && isDraggableCron(job.effectiveCron);

  // Pointer clientX → minute [0,59], guarding NaN and a zero-width rect (§5.1).
  function minuteFromClientX(clientX: number): number | null {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    const vbX = ((clientX - rect.left) / rect.width) * VIEW_W;
    if (!Number.isFinite(vbX)) return null;
    return clampMinute(Math.floor(vbX / (VIEW_W / 60)));
  }

  function onSegPointerDown(e: ReactPointerEvent, m: number) {
    // Left-button drag of a fire segment reschedules it. A click without
    // crossing a minute boundary stays a no-op (endDrag checks to !== from),
    // so plain hover/click is unaffected — no modifier key required.
    if (!editable || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    svgRef.current?.setPointerCapture(e.pointerId);
    setDrag({ from: m, to: m });
  }

  function onPointerMove(e: ReactPointerEvent) {
    if (!drag) return;
    const to = minuteFromClientX(e.clientX);
    if (to === null) return;
    setDrag((d) => (d ? { ...d, to } : d));
    onInspect?.(`${job.jobId} · drag → :${String(to).padStart(2, '0')} (release to stage)`);
  }

  function endDrag(e: ReactPointerEvent) {
    if (!drag) return;
    svgRef.current?.releasePointerCapture?.(e.pointerId);
    if (drag.to !== drag.from) onReschedule?.(job.jobId, drag.from, drag.to);
    setDrag(null);
    onInspect?.(null);
  }

  function onSegKeyDown(e: ReactKeyboardEvent, m: number) {
    if (!editable || !e.shiftKey) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const to = clampMinute(m + (e.key === 'ArrowRight' ? 1 : -1));
    if (to !== m) onReschedule?.(job.jobId, m, to);
  }

  const fireLabel =
    job.fires.length === 0
      ? 'no minutes (disabled)'
      : job.fires.map((m) => `:${String(m).padStart(2, '0')}`).join(', ');
  return (
    <svg
      ref={svgRef}
      viewBox="0 0 660 24"
      preserveAspectRatio="none"
      className="block h-6 w-full select-none"
      role="img"
      aria-label={`${job.level} schedule track for ${job.label}: fires at ${fireLabel}${editable ? '. Drag a fire, or focus one and use Shift+Arrow, to reschedule.' : ''}`}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <rect x={0} y={10} width={VIEW_W} height={4} rx={2} fill={C.trackBg} />

      {zoom === 'hour' &&
        Array.from({ length: 13 }, (_, i) => i * 5).map((m) => (
          <line key={m} x1={minuteX(m)} x2={minuteX(m)} y1={7} y2={17} stroke={C.ghost} strokeWidth={0.5} />
        ))}

      {zoom === 'hour' &&
        job.fires.map((m) => {
          const { x, width, streams } = segmentGeom(m, job.level);
          const conflicting = conflictMinutes.has(m);
          return (
            <g
              key={m}
              style={{ cursor: editable ? 'grab' : 'pointer' }}
              tabIndex={editable ? 0 : -1}
              role={editable ? 'button' : undefined}
              aria-label={editable ? `Fire at minute ${m}. Shift+Arrow to reschedule.` : undefined}
              onMouseEnter={() => onSegHover?.({ minute: m, midPct: ((x + width / 2) / VIEW_W) * 100, conflicting })}
              onMouseLeave={() => onSegHover?.(null)}
              onPointerDown={(e) => onSegPointerDown(e, m)}
              onKeyDown={(e) => onSegKeyDown(e, m)}
            >
              <rect x={x} y={y - 2} width={width} height={stroke + 4} rx={(stroke + 4) / 2} fill={glowColor(job.step)} />
              <rect x={x} y={y} width={width} height={stroke} rx={stroke / 2} fill={color} fillOpacity={streams ? 0.55 : 1}>
                <title>
                  {job.level} · {streams ? 'streams from' : 'fires at'} :{String(m).padStart(2, '0')}
                  {conflicting ? ` · conflict on ${job.queue}` : ''}
                </title>
              </rect>
              {conflicting && (
                <rect x={x - 1} y={y - 3} width={2.5} height={stroke + 6} rx={1} fill={C.red} role="img" aria-label={`Conflict at minute ${m}`} />
              )}
            </g>
          );
        })}

      {/* Drag ghost: dashed marker at the prospective drop minute. */}
      {zoom === 'hour' && drag && (
        <rect
          x={minuteX(drag.to)}
          y={y - 3}
          width={Math.max(MIN_SEGMENT_PX, segmentGeom(drag.to, job.level).width)}
          height={stroke + 6}
          rx={(stroke + 6) / 2}
          fill="none"
          stroke={C.cyan}
          strokeWidth={1.2}
          strokeDasharray="3 2"
        />
      )}

      {zoom === 'day' &&
        Array.from({ length: 24 }, (_, h) => {
          const cx = (h + 0.5) * (VIEW_W / 24);
          const w = DAY_SEGMENT_W[job.level];
          return <rect key={h} x={cx - w / 2} y={y} width={w} height={stroke} rx={stroke / 2} fill={color} opacity={0.7} />;
        })}

      {zoom === 'week' &&
        Array.from({ length: 7 }, (_, d) => {
          const cx = (d + 0.5) * (VIEW_W / 7);
          const w = WEEK_SEGMENT_W[job.level];
          return <rect key={d} x={cx - w / 2} y={y} width={w} height={stroke} rx={stroke / 2} fill={color} opacity={0.65} />;
        })}
    </svg>
  );
}

const LEVEL_DESC: Record<JobLevel, string> = {
  L1: 'reference · fast',
  L2: 'stats · mid-season',
  L3: 'play-by-play',
  L4: 'streaming',
};
const LEVELS: JobLevel[] = ['L1', 'L2', 'L3', 'L4'];

function LevelBadge({ level, dim }: { level: JobLevel; dim?: boolean }) {
  return (
    <span
      className="shrink-0 rounded px-1 py-0.5 font-mono text-[9px] font-medium tracking-wide"
      style={{ background: C.ghost, color: C.textSecondary, opacity: dim ? 0.6 : 1 }}
      title={`${level} — ${LEVEL_DESC[level]}`}
    >
      {level}
    </span>
  );
}

// Legend mapping each tier to its segment length, drawn on the same px scale as
// the hour track (60 min → 64px sample) so the lengths read identically.
function LevelLegend() {
  const sample = (level: JobLevel) =>
    Math.max(MIN_SEGMENT_PX, (Math.min(60, LEVEL_DURATION_MINUTES[level]) / 60) * 64);
  return (
    <div
      className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-lg border px-4 py-2.5"
      style={{ background: C.surface, borderColor: C.border }}
    >
      <span className="text-[10px] font-medium tracking-wider" style={{ color: C.textTertiary }}>
        SEGMENT LENGTH = RUN DURATION
      </span>
      {LEVELS.map((level) => (
        <div key={level} className="flex items-center gap-2">
          <LevelBadge level={level} />
          <svg width={sample(level)} height={6} className="shrink-0">
            <rect x={0} y={1} width={sample(level)} height={4} rx={2} fill={C.textSecondary} />
          </svg>
          <span className="text-[10px]" style={{ color: C.textTertiary }}>
            {LEVEL_DESC[level]}
          </span>
        </div>
      ))}
    </div>
  );
}

function InspectorStrip({ text }: { text: string | null }) {
  return (
    <div
      aria-live="polite"
      className="fixed bottom-0 left-0 right-0 z-30 border-t px-6 py-2.5 backdrop-blur lg:left-60"
      style={{ background: 'rgba(14,22,38,0.92)', borderColor: C.border }}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-2.5 text-[11px]" style={{ color: C.textSecondary }}>
        <Info className="h-3.5 w-3.5 shrink-0" style={{ color: C.cyan }} />
        <span className="truncate">{text ?? 'Hover a heatmap column or a job track for schedule details.'}</span>
      </div>
    </div>
  );
}

function PendingChangesBar({
  count,
  conflicts,
  disabled,
  disabledReason,
  onDiscard,
  onReview,
}: {
  count: number;
  conflicts: number;
  disabled: boolean;
  disabledReason?: string;
  onDiscard: () => void;
  onReview: () => void;
}) {
  return (
    <div
      className="fixed bottom-11 left-0 right-0 z-40 border-t px-6 py-2.5 backdrop-blur lg:left-60"
      style={{ background: 'rgba(17,27,48,0.96)', borderColor: C.border }}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3">
        <span
          className="rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ background: 'rgba(94,227,247,0.12)', color: C.cyan }}
        >
          {count} pending change{count === 1 ? '' : 's'}
        </span>
        <span className="text-[11px]" style={{ color: C.textSecondary }}>
          {conflicts} same-queue conflict{conflicts === 1 ? '' : 's'} after these edits
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" onClick={onDiscard}>Discard</Button>
          <Button onClick={onReview} disabled={disabled} title={disabledReason}>
            Review &amp; commit
          </Button>
        </div>
      </div>
    </div>
  );
}

function CommitConfirmationModal({
  changes,
  committing,
  onCancel,
  onConfirm,
}: {
  changes: CronChange[];
  committing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    previewCronChanges(changes)
      .then((p) => active && setPreview(p))
      .catch((e) => active && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [changes]);

  const hasInvalid = (preview?.invalid.length ?? 0) > 0;
  const deltaColor = (before: number, after: number) =>
    after > before ? C.red : after < before ? '#4ADE80' : C.textSecondary;

  return (
    <Dialog
      open
      onClose={onCancel}
      title="Commit schedule changes"
      description={`${changes.length} job${changes.length === 1 ? '' : 's'} · live reschedule on save`}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={committing}>
            <X className="h-4 w-4" /> Cancel
          </Button>
          <Button onClick={onConfirm} disabled={committing || loading || !!error || hasInvalid}>
            {committing ? 'Committing…' : 'Commit & reschedule'}
          </Button>
        </>
      }
    >
      {loading ? (
        <Skeleton className="h-24 w-full" />
      ) : error ? (
        <div className="text-xs" style={{ color: C.red }}>
          Preview failed: {error}
        </div>
      ) : preview ? (
        <div className="space-y-3 text-xs">
          <div className="flex items-center gap-2">
            <span style={{ color: C.textSecondary }}>Total same-queue conflicts:</span>
            <span className="font-medium">{preview.before.pairCount}</span>
            <span style={{ color: C.textTertiary }}>→</span>
            <span className="font-medium" style={{ color: deltaColor(preview.before.pairCount, preview.after.pairCount) }}>
              {preview.after.pairCount}
            </span>
          </div>

          {hasInvalid && (
            <div className="rounded-md border px-2 py-1.5" style={{ borderColor: 'rgba(248,113,113,0.4)', color: '#FCA5A5' }}>
              {preview.invalid.map((iv) => (
                <div key={iv.jobId}>
                  <span className="font-mono">{iv.jobId}</span>: {iv.error}
                </div>
              ))}
            </div>
          )}

          <div className="divide-y rounded-md border" style={{ borderColor: C.border }}>
            {preview.perJob.map((p) => (
              <div key={p.jobId} className="grid grid-cols-[1fr_auto] items-center gap-2 px-2.5 py-1.5">
                <div className="min-w-0">
                  <div className="truncate font-mono text-[11px]">{p.jobId}</div>
                  <div className="truncate font-mono text-[10px]" style={{ color: C.textTertiary }}>
                    {p.effectiveCronBefore} → <span style={{ color: C.cyan }}>{p.effectiveCronAfter}</span>
                  </div>
                </div>
                <div className="whitespace-nowrap text-right text-[10px]" style={{ color: C.textSecondary }}>
                  {p.conflictMinutesBefore.length} →{' '}
                  <span style={{ color: deltaColor(p.conflictMinutesBefore.length, p.conflictMinutesAfter.length) }}>
                    {p.conflictMinutesAfter.length}
                  </span>{' '}
                  conflict min
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

interface EditForm {
  cron: string;
  queue: string;
  enabled: boolean;
  reason: string;
}

function EditOverrideDialog({
  job,
  canReset,
  resetting,
  onReset,
  onClose,
  onSave,
}: {
  job: JobRowData;
  canReset: boolean;
  resetting: boolean;
  onReset: () => Promise<void>;
  onClose: () => void;
  onSave: (form: EditForm) => Promise<void>;
}) {
  const [form, setForm] = useState<EditForm>({
    cron: job.effectiveCron,
    queue: job.override?.queue ?? job.queue,
    enabled: job.enabled,
    reason: job.override?.reason ?? '',
  });
  const [saving, setSaving] = useState(false);
  const desc = describeCron(form.cron);

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Edit ${job.label}`}
      description={<span className="font-mono">{job.jobId}</span>}
      footer={
        <>
          {canReset && (
            <Button
              variant="ghost"
              className="mr-auto text-muted-foreground"
              disabled={saving || resetting}
              onClick={onReset}
            >
              <RotateCcw className="h-4 w-4" /> {resetting ? 'Resetting…' : 'Reset to default'}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={saving || resetting}>
            <X className="h-4 w-4" /> Cancel
          </Button>
          <Button
            disabled={saving || resetting || !desc.ok}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(form);
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? 'Saving…' : 'Save override'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="cron">Cron expression</Label>
          <Input
            id="cron"
            value={form.cron}
            onChange={(e) => setForm({ ...form, cron: e.target.value })}
            className="font-mono"
          />
          <div className={`text-[11px] ${desc.ok ? 'text-muted-foreground' : 'text-destructive'}`}>
            {desc.ok ? desc.text : desc.error}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="queue">Queue</Label>
          <Select id="queue" value={form.queue} onChange={(e) => setForm({ ...form, queue: e.target.value })}>
            <option value="harvest-genius">harvest-genius</option>
            <option value="harvest-genius-sync">harvest-genius-sync</option>
            <option value="harvest-presto">harvest-presto</option>
            <option value="harvest-presto-sync">harvest-presto-sync</option>
            <option value="default">default</option>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="enabled"
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            className="h-4 w-4 rounded border-border bg-background"
          />
          <Label htmlFor="enabled">Enabled</Label>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="reason">Reason (optional)</Label>
          <Textarea
            id="reason"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Why this override exists — visible to other admins."
          />
        </div>
      </div>
    </Dialog>
  );
}
